import { useEffect, useState } from "react";
import { api, formatApiError, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const CHAIN_ROLES = ["ho_approver", "director", "finance", "accounts"];

export default function ApprovalMatrix() {
  const { user } = useAuth();
  const [slabs, setSlabs] = useState([]);
  const [busy, setBusy] = useState(false);
  const canEdit = user?.role === "admin";
  useEffect(() => {
    api.get("/approval-matrix").then((r) => setSlabs(r.data.slabs || []));
  }, []);

  const setSlab = (i, patch) =>
    setSlabs((s) => s.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const addSlab = () => setSlabs((s) => [...s, { min_amount: 0, max_amount: null, chain: ["ho_approver"], label: "" }]);
  const removeSlab = (i) => setSlabs((s) => s.filter((_, idx) => idx !== i));
  const setChain = (i, roleIdx, role) => {
    const row = slabs[i];
    const chain = [...row.chain];
    chain[roleIdx] = role;
    setSlab(i, { chain });
  };
  const addChainRole = (i) => setSlab(i, { chain: [...slabs[i].chain, "director"] });
  const removeChainRole = (i, roleIdx) =>
    setSlab(i, { chain: slabs[i].chain.filter((_, x) => x !== roleIdx) });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/approval-matrix", { slabs });
      toast.success("Approval matrix saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="matrix-root">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Configuration</div>
        <h1 className="font-heading text-4xl mt-1 tracking-tight">Approval Matrix</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Amount-based approval slabs. Each new expense is routed through the chain of roles for its slab, in order.
        </p>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="p-4 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500 grid grid-cols-12 gap-4">
          <div className="col-span-1">#</div>
          <div className="col-span-2">Min Amount</div>
          <div className="col-span-2">Max Amount</div>
          <div className="col-span-2">Label</div>
          <div className="col-span-4">Approval Chain (in order)</div>
          <div className="col-span-1"></div>
        </div>
        {slabs.map((s, i) => (
          <div key={`slab-${s.min_amount}-${s.max_amount ?? "inf"}`} className="p-4 border-b border-zinc-100 grid grid-cols-12 gap-4 items-start" data-testid={`matrix-slab-${i}`}>
            <div className="col-span-1 pt-2 font-mono text-sm">{i + 1}</div>
            <div className="col-span-2">
              <Input type="number" value={s.min_amount}
                     onChange={(e) => setSlab(i, { min_amount: Number(e.target.value) })}
                     disabled={!canEdit}
                     className="rounded-sm h-10 tabular-nums text-right" />
            </div>
            <div className="col-span-2">
              <Input type="number" value={s.max_amount ?? ""} placeholder="∞ (no upper limit)"
                     onChange={(e) => setSlab(i, { max_amount: e.target.value === "" ? null : Number(e.target.value) })}
                     disabled={!canEdit}
                     className="rounded-sm h-10 tabular-nums text-right" />
            </div>
            <div className="col-span-2">
              <Input value={s.label || ""} onChange={(e) => setSlab(i, { label: e.target.value })}
                     disabled={!canEdit}
                     placeholder="e.g. Small purchases"
                     className="rounded-sm h-10" />
            </div>
            <div className="col-span-4 space-y-2">
              {s.chain.map((role, ri) => (
                <div key={`${role}-${ri}`} className="flex gap-2 items-center">
                  <span className="text-xs text-zinc-400 w-6">{ri + 1}.</span>
                  <Select value={role} onValueChange={(v) => setChain(i, ri, v)} disabled={!canEdit}>
                    <SelectTrigger className="rounded-sm h-9 flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CHAIN_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLES[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {canEdit && s.chain.length > 1 && (
                    <button onClick={() => removeChainRole(i, ri)} className="text-zinc-400 hover:text-rose-600 p-1">
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              ))}
              {canEdit && (
                <button onClick={() => addChainRole(i)} className="text-xs text-zinc-600 hover:text-black uppercase tracking-widest">+ Add step</button>
              )}
            </div>
            <div className="col-span-1 pt-2">
              {canEdit && (
                <button onClick={() => removeSlab(i)} className="text-zinc-400 hover:text-rose-600" data-testid={`remove-slab-${i}`}>
                  <Trash size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="flex justify-between">
          <Button variant="outline" onClick={addSlab} className="rounded-sm border-zinc-300 gap-2" data-testid="add-slab">
            <Plus size={14} /> Add Slab
          </Button>
          <Button onClick={save} disabled={busy} data-testid="save-matrix"
                  className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
            {busy ? "Saving…" : "Save Matrix"}
          </Button>
        </div>
      )}
    </div>
  );
}
